import { useAuth } from '@/contexts/auth-context';
import api from '@/libs/axiosInstance';
import { Alert } from '@/components/ui/alert';
import { Avatar } from '@/components/ui/avatar';
import { LoadingPage } from '@/components/ui/loading-page';
import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { Logo } from '@fluenceur/ui';
import { motion } from 'framer-motion';
import { Puzzle } from 'iconoir-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const MotionBox = motion.create(Box);

/**
 * Dedicated page for the Chrome Extension authorize flow.
 *
 * Handles two cases:
 * - Already logged in: shows a confirmation screen with user info + "Connect Extension" button
 * - Not logged in: shows a "Log in to connect" button that redirects to login with returnTo
 */
export default function ExtensionAuthorizePage() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, refreshCurrentUser, user } = useAuth();
  const navigate = useNavigate();
  const [hasAttemptedAuth, setHasAttemptedAuth] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated && !isLoading && !hasAttemptedAuth) {
      setHasAttemptedAuth(true);
      refreshCurrentUser({ force: false }).catch(() => {});
    }
  }, [isAuthenticated, isLoading, hasAttemptedAuth, refreshCurrentUser]);

  const handleConnect = async () => {
    setAuthorizing(true);
    setError(null);
    try {
      const { data: response } = await api.post('/auth/extension/authorize');
      const code = response.data?.code;
      if (code) {
        navigate(`/extension/callback?code=${code}`, { replace: true });
      } else {
        setError(t('extension.authorizeFailed'));
        setAuthorizing(false);
      }
    } catch {
      setError(t('extension.authorizeFailed'));
      setAuthorizing(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate(`/?returnTo=${encodeURIComponent('/extension/authorize')}`, { replace: true });
  };

  // Still loading auth state
  if (isLoading || (!isAuthenticated && !hasAttemptedAuth)) {
    return <LoadingPage bg="babyPowder" />;
  }

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';

  return (
    <Flex minH="100vh" align="center" justify="center" bg="babyPowder" px={4}>
      <MotionBox
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        w="full"
        maxW="400px"
      >
        <VStack gap={0} textAlign="center">
          {/* Header */}
          <Logo h="28px" mb={8} />

          <Box
            bg="white"
            borderWidth="1px"
            borderColor="light-gray-2"
            rounded={{ base: 'xl', md: '2xl' }}
            shadow="card"
            w="full"
            p={{ base: 5, md: 7 }}
          >
            <VStack gap={5}>
              {/* Chrome icon badge */}
              <Flex w="48px" h="48px" rounded="xl" bg="gray.100" align="center" justify="center">
                <Puzzle width={24} height={24} color="#4f4f4f" />
              </Flex>

              {/* Title & description */}
              <VStack gap={1.5}>
                <Text fontFamily="heading" fontWeight="semibold" fontSize="lg" color="dark">
                  {t('extension.connectTitle')}
                </Text>
                <Text color="fg.muted" fontSize="sm" lineHeight="tall">
                  {t('extension.connectDescription')}
                </Text>
              </VStack>

              {/* User info card (logged in only) */}
              {isAuthenticated && user && (
                <Box
                  w="full"
                  p={4}
                  bg="gray.50"
                  rounded="xl"
                  borderWidth="1px"
                  borderColor="gray.100"
                >
                  <HStack gap={3} align="center">
                    <Avatar name={displayName} src={user.avatar?.fileUrl} size="md" />
                    <VStack align="start" gap={0.5}>
                      <Text fontWeight="medium" fontSize="sm" color="dark" lineHeight="short">
                        {displayName}
                      </Text>
                      <Text color="fg.muted" fontSize="xs" lineHeight="short">
                        {user.email}
                      </Text>
                      {user.currentBrand && (
                        <Text color="fg.muted" fontSize="xs" lineHeight="short">
                          {user.currentBrand.name}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                </Box>
              )}

              {/* Error alert */}
              {error && <Alert status="error" title={error} />}

              {/* Action button */}
              {isAuthenticated ? (
                <Button
                  colorPalette="gray"
                  size="lg"
                  w="full"
                  h={12}
                  onClick={handleConnect}
                  loading={authorizing}
                  loadingText={t('extension.authorizingMessage')}
                >
                  {t('extension.connectButton')}
                </Button>
              ) : (
                <Button colorPalette="gray" size="lg" w="full" h={12} onClick={handleLoginRedirect}>
                  {t('extension.loginToConnect')}
                </Button>
              )}
            </VStack>
          </Box>

          {/* "Not you?" link (logged in only) */}
          {isAuthenticated && (
            <Text
              color="fg.muted"
              fontSize="xs"
              mt={4}
              cursor="pointer"
              _hover={{ color: 'dark', textDecoration: 'underline' }}
              transition="colors 0.2s"
              onClick={handleLoginRedirect}
            >
              {t('extension.notYou')}
            </Text>
          )}
        </VStack>
      </MotionBox>
    </Flex>
  );
}
